import AttachEmailOutlinedIcon from "@mui/icons-material/AttachEmailOutlined"
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import Button from "@mui/material/Button"
import IconButton from "@mui/material/IconButton"
import Typography from "@mui/material/Typography"
import { useRef, useState } from "react"
import { FileIcon, defaultStyles } from "react-file-icon"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { trimLongString } from "../../../common/utils"
import { getFileExtension } from "../utils"

const DEFAULT_MAX_ATTACHMENT_COUNT = 3
const MAX_ATTACHMENT_BYTES = 50 * 1024 * 1024

export default function AddAttachmentsSection({
  fileAttachments,
  setFileAttachments,
  fileAttachmentError,
  setFileAttachmentError,
  setDeletedAttachments,
  maxAttachmentCount = DEFAULT_MAX_ATTACHMENT_COUNT,
}) {
  const hasAttachmentCountLimit = maxAttachmentCount != null
  const fileInputRef = useRef()

  function handleAddFileAttachmentButtonClicked() {
    fileInputRef.current.click()
  }

  function handleFileInputChanged(e) {
    const selectedFiles = Array.from(e.target.files || [])
    e.target.value = ""
    if (!selectedFiles.length) {
      return
    }

    const acceptedFiles = []
    let error = null

    for (const file of selectedFiles) {
      const nextCount = fileAttachments.length + acceptedFiles.length
      if (hasAttachmentCountLimit && nextCount >= maxAttachmentCount) {
        error = `Maximum ${maxAttachmentCount} files allowed.`
        break
      }
      if (file.size > MAX_ATTACHMENT_BYTES) {
        error = "File is too large (> 50 mb)"
        continue
      }
      if (!file.type) {
        error = "Unable to determine file type. Please select another file"
        continue
      }
      acceptedFiles.push(file)
    }

    if (acceptedFiles.length) {
      setFileAttachments((currentAttachments) => [
        ...currentAttachments,
        ...acceptedFiles,
      ])
      if (!error) {
        setFileAttachmentError(null)
      }
    }
    if (error) {
      setFileAttachmentError(error)
    }
  }

  function handleDeleteAttachedFileButtonClicked(index) {
    setFileAttachments(fileAttachments.filter((file, i) => i !== index))
    const deletedAttachment = fileAttachments[index]
    // don't submit deleted attachment if removing files not yet submitted (on creation)
    if (isFile(deletedAttachment)) {
      return
    }
    setDeletedAttachments((deletedAttachments) => [
      ...deletedAttachments,
      deletedAttachment.id,
    ])
  }

  return (
    <Column sx={{ gap: 1 }}>
      {fileAttachments.length > 0 && (
        <Column sx={{ gap: 1, mt: 1 }}>
          <Row sx={{ alignItems: "center", gap: 1 }}>
            <AttachEmailOutlinedIcon />
            <Typography variant="h5">
              Attached files ({fileAttachments.length})
            </Typography>
          </Row>
          <Column sx={{ gap: 1 }}>
            {fileAttachments.map((file, i) => (
              <Row key={i} sx={{ alignItems: "center", gap: 1 }}>
                <Row sx={{ width: "16px" }}>
                  <FileIcon
                    extension={getFileExtension(file.name)}
                    {...defaultStyles[getFileExtension(file.name)]}
                  />
                </Row>
                <Typography variant="body2" sx={{ fontSize: "12px" }}>
                  {trimLongString(file.name, 50)}
                </Typography>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDeleteAttachedFileButtonClicked(i)}
                >
                  <CloseOutlinedIcon sx={{ fontSize: "11px" }} />
                </IconButton>
              </Row>
            ))}
            {fileAttachmentError && (
              <Typography
                variant="body1"
                sx={{ fontWeight: "bold", color: "error.main", mt: 1 }}
              >
                {fileAttachmentError}
              </Typography>
            )}
          </Column>
        </Column>
      )}
      <Column sx={{ gap: 0.5 }}>
        <Button
          variant="text"
          color="primary"
          size="large"
          startIcon={<AttachEmailOutlinedIcon />}
          fullWidth
          disabled={
            hasAttachmentCountLimit &&
            fileAttachments.length >= maxAttachmentCount
          }
          onClick={handleAddFileAttachmentButtonClicked}
        >
          Add file attachment
        </Button>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileInputChanged}
          style={{ display: "none" }}
        />
        <Row sx={{ alignItems: "center", gap: 0.5, alignSelf: "center" }}>
          <InfoOutlinedIcon sx={{ fontSize: "17px" }} />
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontSize: "12px" }}
          >
            {hasAttachmentCountLimit
              ? `Max. file size 50mb. ${maxAttachmentCount} files max. allowed.`
              : "Max. file size 50mb."}
          </Typography>
        </Row>
      </Column>
    </Column>
  )
}

export function useAddAttachmentSectionState() {
  const [fileAttachments, setFileAttachments] = useState([])
  const [fileAttachmentError, setFileAttachmentError] = useState(null)
  const [deletedAttachments, setDeletedAttachments] = useState([])

  function addAttachmentsToFormData(formData: FormData) {
    for (const file of fileAttachments) {
      // we do use fileAttachments for backend attachments which are not Files and shouldn't be submitted
      if (!isFile(file)) {
        continue
      }
      formData.append("attachments", file, file.name)
    }
    for (const id of deletedAttachments) {
      formData.append("deleted_attachments", id)
    }
  }

  function convertBackendAttachmentsToFileAttachments(attachments) {
    setFileAttachments(
      attachments.map((attachment) => {
        const urlParts = attachment.file.split("/")
        return { ...attachment, name: urlParts[urlParts.length - 1] }
      })
    )
  }

  return {
    fileAttachments,
    setFileAttachments,
    fileAttachmentError,
    setFileAttachmentError,
    setDeletedAttachments,
    addAttachmentsToFormData,
    convertBackendAttachmentsToFileAttachments,
  }
}

function isFile(attachment) {
  return attachment instanceof File
}
